import { type Application, type Shift } from "@/lib/backend/api";
import { DEFAULT_TIMEZONE } from "@/lib/constants";
import { type DayKey } from "@/lib/discover-window";
import { toDayKey } from "@/utils/format-datetime";

/**
 * What is going on for _you_ on a day, in the one word the dot has room for.
 *
 * The calendar on Ontdek used to answer a single question — "is there work here I can still apply
 * for?" — which is exactly half of what someone standing in front of it wants to know. The other
 * half is what they already did about that day: applied, got planned in, asked to be let off. A
 * marketplace that cannot show you your own footprint sends you into the list to find out.
 *
 * ## One dot, one answer
 *
 * A day can hold several of these at once: planned in at one project, applied at another, and a
 * third still open. Painting all of them would give a five-pixel dot three colours; instead the day
 * takes the first state in {@link DAY_STATE_PRIORITY} it actually has.
 *
 * The order is _what the day asks of you_, strongest first:
 *
 * 1. `scheduled` — you work that day. Nothing outranks a commitment you already made.
 * 2. `cancellation` — you asked to be let off and are waiting on them.
 * 3. `applied` — you said yes and are waiting on them.
 * 4. `available` — nothing of yours, but work you could still take.
 * 5. `cancelled` — it came off. Last, because a day that also holds open work is better described by
 *    that work than by the thing that fell through.
 *
 * `invited` stond hier tussen `scheduled` en `cancellation` als de enige stand die een taak was.
 * Uitnodigingen liepen via campagnes en zijn met het communicatieblok verdwenen (stap 4 van
 * `admin/docs/communicatie-verwijderen.md`).
 */
export type DayState = "scheduled" | "cancellation" | "applied" | "available" | "cancelled";

export const DAY_STATE_PRIORITY = [
  "scheduled",
  "cancellation",
  "applied",
  "available",
  "cancelled",
] as const satisfies readonly DayState[];

const RANK = new Map<DayState, number>(DAY_STATE_PRIORITY.map((state, index) => [state, index]));

/**
 * Where an application stands, from the employee's side.
 *
 * `ACCEPTED`/`CONSUMED` land on `scheduled` rather than getting a state of their own: from the
 * employee's chair an accepted application _is_ being planned in, and the shift it becomes carries
 * the same colour. Two names for one fact would only make the dot mean less.
 */
function stateOfApplication(status: Application["status"]): DayState {
  switch (status) {
    case "ACCEPTED":
    case "CONSUMED":
      return "scheduled";
    case "CANCELLATION_REQUESTED":
    case "REVOCATION_REQUESTED":
      return "cancellation";
    case "CANCELLED":
    case "REJECTED":
      return "cancelled";
    case "PENDING":
    case "RESERVE":
      return "applied";
  }
}

/**
 * The day an instant falls on, in the timezone the work happens in.
 *
 * Not the device's timezone and not UTC. A shift that starts at 00:30 Amsterdam time is
 * `2026-08-14T22:30:00Z` — read as UTC it lands a day early, and read on a phone still set to a
 * holiday timezone it lands wherever that phone is. `Europe/Amsterdam` is the day the planner and
 * the employee both mean, and it is the same zone the rest of the app groups shifts by.
 */
export function dayKeyOf(instant: string): DayKey {
  // `tzDate(...).getDate()` stond hier: dat leest de velden van een Date in de tijdzone van het
  // toestel, en tzDate telt de afwijking er nog een tweede keer bij op. `toDayKey` rekent de dag
  // rechtstreeks in de doeltijdzone uit.
  return toDayKey(instant, DEFAULT_TIMEZONE);
}

export type DayStateSources = {
  /** Your own shifts. `stats.hasPendingCancellation` is what makes one an afmeldverzoek. */
  shifts: readonly Shift[];
  /** Your own applications, whatever state they are in. */
  applications: readonly Application[];
  /** Days that hold work you can still apply for, under the filters currently set. */
  availableDays: ReadonlySet<DayKey>;
};

/**
 * One state per day, strongest wins.
 *
 * Pure on purpose: the priority rules are the whole point of this module and they are far easier to
 * pin down here than through four queries and a rendered grid.
 */
export function buildDayStates({
  shifts,
  applications,
  availableDays,
}: DayStateSources): Map<DayKey, DayState> {
  const states = new Map<DayKey, DayState>();

  const claim = (day: DayKey, state: DayState) => {
    const held = states.get(day);
    if (held === undefined || RANK.get(state)! < RANK.get(held)!) {
      states.set(day, state);
    }
  };

  for (const day of availableDays) {
    claim(day, "available");
  }

  for (const shift of shifts) {
    if (!shift.startAt) {
      continue;
    }
    claim(
      dayKeyOf(shift.startAt),
      shift.stats?.hasPendingCancellation ? "cancellation" : "scheduled",
    );
  }

  for (const application of applications) {
    const startAt = application.availabilitySlot?.startAt;
    if (!startAt) {
      continue;
    }
    claim(dayKeyOf(startAt), stateOfApplication(application.status));
  }

  return states;
}
